import { createSlice } from "@reduxjs/toolkit";
import { 
  fetchAiNlpPropertiesThunk, 
  endChatSessionThunk,
  fetchChatHistoryThunk,
  loadChatSessionThunk 
} from "../thunks/aiNlpThunk";

const initialState = {
  nlpSearchResults: null,
  nlpSearchSummary: null,
  sessionId: null,
  nlpStatus: "idle",
  nlpError: null,
  conversationHistory: [],
  userLocation: null,
  locationPermission: "pending",
  sessionStatus: "idle",
  sessionError: null,
  chatHistory: [],
  chatHistoryStatus: "idle",
  chatHistoryError: null,
};

const aiNlpSlice = createSlice({
  name: "aiNlp",
  initialState,
  reducers: {
    clearConversation: (state) => {
      state.conversationHistory = [];
      state.nlpSearchResults = null;
      state.nlpSearchSummary = null;
      state.sessionId = null;
      state.nlpStatus = "idle";
      state.nlpError = null;
      state.sessionStatus = "idle";
      state.sessionError = null;
    },
    setUserLocation: (state, action) => {
      state.userLocation = action.payload;
    },
    setLocationPermission: (state, action) => {
      state.locationPermission = action.payload;
    },
    setSessionId: (state, action) => {
      state.sessionId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAiNlpPropertiesThunk.pending, (state, action) => {
        state.nlpStatus = "loading";
        state.conversationHistory.push({
          role: "user",
          content: action.meta.arg.prompt,
          timestamp: Date.now(),
        });
      })
      .addCase(fetchAiNlpPropertiesThunk.fulfilled, (state, action) => {
        state.nlpStatus = "succeeded";
        state.nlpSearchResults = action.payload.listings;
        state.nlpSearchSummary = action.payload.summary;

        if (action.payload.userLocation) {
          state.userLocation = action.payload.userLocation;
        }

        state.conversationHistory.push({
          role: "assistant",
          content: action.payload.message || action.payload.summary,
          timestamp: Date.now(),
          listings: action.payload.listings,
          parameters: action.payload.parameters,
          error: action.payload.error,
          questions: action.payload.questions
        });

        if (action.payload.needsLocation) {
          state.conversationHistory.push({
            role: "assistant",
            content: "Please specify which city you're looking in, or allow location access.",
            timestamp: Date.now(),
          });
        }
      })
      .addCase(fetchAiNlpPropertiesThunk.rejected, (state, action) => {
        state.nlpStatus = "failed";
        state.nlpError = action.error.message;
        
        state.conversationHistory.push({
          role: "assistant",
          content: "I apologize, but I encountered an error while searching for properties. Please try again.",
          timestamp: Date.now(),
          error: true,
        });
      })
      .addCase(endChatSessionThunk.pending, (state) => {
        state.sessionStatus = "loading";
      })
      .addCase(endChatSessionThunk.fulfilled, (state) => {
        state.sessionStatus = "succeeded";
      })
      .addCase(endChatSessionThunk.rejected, (state, action) => {
        state.sessionStatus = "failed";
        state.sessionError = action.error.message;
      })
      .addCase(fetchChatHistoryThunk.pending, (state) => {
        state.chatHistoryStatus = "loading";
      })
      .addCase(fetchChatHistoryThunk.fulfilled, (state, action) => {
        state.chatHistoryStatus = "succeeded";
        state.chatHistory = action.payload;
      })
      .addCase(fetchChatHistoryThunk.rejected, (state, action) => {
        state.chatHistoryStatus = "failed";
        state.chatHistoryError = action.error.message;
      })
      .addCase(loadChatSessionThunk.pending, (state) => {
        state.nlpStatus = "loading";
      })
      .addCase(loadChatSessionThunk.fulfilled, (state, action) => {
        state.nlpStatus = "succeeded";
        state.sessionId = action.payload.sessionId;
        state.conversationHistory = action.payload.messages;
      })
      .addCase(loadChatSessionThunk.rejected, (state, action) => {
        state.nlpStatus = "failed";
        state.nlpError = action.error.message;
      });
  },
});

export const { clearConversation, setUserLocation, setLocationPermission, setSessionId } =
  aiNlpSlice.actions;

export default aiNlpSlice.reducer;
